"use client";

import Button from "@/components/atoms/buttons/Button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn, getAddressAttributes, getAddressMetadata, getDifferentAddress } from "@/lib/utils";
import { createAccountLocalisedSchema, BillingAddressSchema } from "@/lib/validation/account";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { StoreCustomer, StoreCustomerAddress } from "@medusajs/types";
import { RefObject, useEffect, useRef, useState, useTransition } from "react";
import { useCustomer } from "@/hooks/useCustomer";
import { toast } from "@/components/atoms/toast";
import { sdk } from "@/lib/medusa/client";
import { Loader2 } from "lucide-react";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { DeliveryAndBillingSchema } from "@/lib/validation/payment";
import { useCompanyDetails } from "@/queries/company";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OptIn from "@/components/atoms/opt-in";

type BillingAddressFormProps = {
  onSuccess: () => void;
  addressToEdit?: StoreCustomerAddress | null;
  addressId?: string;
  locale: Locales;
  accountTypeValue: DeliveryAndBillingSchema["accountType"];
  showPrefillCheckbox?: boolean;
  shippingAddressValue?: string;
};

const BillingAddressForm = ({
  onSuccess,
  addressId,
  addressToEdit,
  locale,
  accountTypeValue,
  showPrefillCheckbox = false,
  shippingAddressValue,
}: BillingAddressFormProps) => {
  const [isPending, startTransition] = useTransition();
  const { setCustomer, customer, refreshCustomer } = useCustomer();
  const metadata = getAddressMetadata(addressToEdit);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: companyDetails } = useCompanyDetails(customer?.company_name || "", customer?.id || "anonymous");

  useOnClickOutside(dropdownRef as RefObject<HTMLDivElement>, () => setShowDropdown(false));

  const { billingAddressSchema } = createAccountLocalisedSchema(locale, accountTypeValue);
  const form = useForm<BillingAddressSchema>({
    resolver: zodResolver(billingAddressSchema),
    defaultValues: {
      addressName: addressToEdit?.address_name || metadata.address_name || "",
      postalCode: addressToEdit?.postal_code || "",
      city: addressToEdit?.city || "",
      postalAddress: addressToEdit?.address_1 || "",
      message: metadata.message || "",
      companyName: addressToEdit?.company || (accountTypeValue === "company" && companyDetails?.name) || "",
      vatNumber: (accountTypeValue === "company" && companyDetails?.vat) || metadata.vat_number || "",
      contactPerson: metadata.contact_person || "",
      countryCode: metadata.country_code || "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const { billingAddress } = getDifferentAddress(customer?.addresses);

  const onSubmit = (data: BillingAddressSchema) => {
    if (!customer) return toast.warning({ title: "Login to add an address" });

    if (!addressId) {
      if (billingAddress && billingAddress.length >= 5) {
        const currentVariantAddresses = billingAddress.filter(
          (address) => address.metadata?.variant === accountTypeValue
        );
        if (currentVariantAddresses.length >= 5) {
          const addressType = accountTypeValue === "company" ? "company" : "individual";
          toast.warning({
            title: __(`You have reached the maximum number of ${addressType} billing addresses allowed.`, locale),
            description: __("Please delete an existing address before adding a new one.", locale),
          });

          return;
        }
      }
    }

    startTransition(async () => {
      if (!companyDetails?.name && accountTypeValue === "company") {
        try {
          await sdk.client.fetch("store/companies", {
            method: "POST",
            body: {
              currency_code: "eur",
              additionalData: {
                customerId: customer?.id,
              },
              name: data.companyName,
              email: customer.email,
              phone: customer.phone,
              address: data.postalAddress,
              city: data.city,
              vat: data.vatNumber,
              zip: data.postalCode,
              created_by: customer.id,
            },
          });
          await refreshCustomer();
        } catch (error) {
          console.error(error);
          toast.error({ title: __("Something went wrong. Please try again later.", locale) });
          return;
        }
      }

      try {
        let updatedCustomer: StoreCustomer;

        if (addressId) {
          // EDIT MODE
          const res = await sdk.store.customer.updateAddress(addressId, {
            address_name: data.addressName,
            address_1: data.postalAddress,
            city: data.city,
            postal_code: data.postalCode,
            company: data.companyName,
            metadata: {
              message: data.message,
              type: "billing",
              address_name: data.addressName,
              vat_number: data.vatNumber,
              contact_person: data.contactPerson,
              country_code: data.countryCode,
            },
          });
          updatedCustomer = res.customer;
          toast.success({ title: __("Address updated successfully", locale) });
        } else {
          // ADD MODE

          const res = await sdk.store.customer.createAddress({
            address_name: data.addressName,
            address_1: data.postalAddress,
            city: data.city,
            postal_code: data.postalCode,
            company: data.companyName,
            metadata: {
              message: data.message,
              type: "billing",
              address_name: data.addressName,
              vat_number: data.vatNumber,
              contact_person: data.contactPerson,
              variant: accountTypeValue,
              country_code: data.countryCode,
            },
          });
          updatedCustomer = res.customer;
          toast.success({ title: __("Billing address added successfully", locale) });
        }

        // Update customer context
        setCustomer(updatedCustomer);
        reset({
          addressName: "",
          postalCode: "",
          city: "",
          postalAddress: "",
          message: "",
        });
        onSuccess();
      } catch (error) {
        toast.error({
          title: __("Error adding billing address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error saving billing address:", error);
      }
    });
  };

  const handleAddressAutocomplete = (value: string) => {
    if (!window.google || !value) {
      setPredictions([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ["be"] },
        types: ["geocode"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(predictions);
          setShowDropdown(predictions.length > 0);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handlePredictionSelect = (placeId: string, description: string) => {
    if (!window.google) return;

    const placesService = new window.google.maps.places.PlacesService(document.createElement("div"));

    placesService.getDetails(
      {
        placeId,
        fields: ["address_components", "geometry"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          console.error("Place details error", status);
          return;
        }

        const components: Record<string, string> = {};

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes("postal_code")) {
            components.postal_code = component.long_name;
          }
          if (types.includes("locality")) {
            components.city = component.long_name;
          }
          if (types.includes("route") || types.includes("street_address")) {
            components.address_1 = component.long_name;
          }
          if (types.includes("country")) {
            components.country_code = component.short_name;
          }
        });

        setValue("postalCode", components.postal_code || "");
        setValue("city", components.city || "");
        setValue("postalAddress", description);
        setValue("countryCode", components.country_code.toLowerCase() || "");
        setPredictions([]);
      }
    );
  };

  const companyNameValue = watch("companyName");

  useEffect(() => {
    if (addressToEdit || accountTypeValue !== "company") return;

    setValue("companyName", companyDetails?.name || "");
    setValue("vatNumber", companyDetails?.vat || "");
  }, [accountTypeValue, addressToEdit, companyDetails?.name, companyDetails?.vat, setValue]);

  useEffect(() => {
    if (accountTypeValue === "company") {
      if (companyNameValue && companyDetails?.name === companyNameValue && companyDetails?.vat) {
        setValue("vatNumber", companyDetails.vat);
      }
    }
  }, [accountTypeValue, companyDetails, companyNameValue, setValue]);

  const { deliveryAddress } = getAddressAttributes(customer?.addresses, undefined, shippingAddressValue);

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        className="border-primary-light/50 mx-5 grid gap-x-5 gap-y-5 border-t pt-5 pb-5.5 md:grid-cols-2"
      >
        <FormField
          control={control}
          name="addressName"
          render={({ field }) => (
            <FormItem className="gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Address name", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-12 placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        {accountTypeValue === "company" && (
          <>
            {companyDetails?.name ? (
              <FormField
                control={control}
                name="companyName"
                render={({ field }) => (
                  <FormItem className="w-full gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="font-cabinet-grotesk text-base font-medium md:font-bold">
                        {__("Company", locale)}
                        <span className="text-pink-primary">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-border-light font-cabinet-grotesk aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 data-[placeholder]:text-dark-primary text-dark-primary h-12 bg-white text-sm font-bold placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px] [&>svg]:size-5">
                            <SelectValue placeholder={__("Select a company", locale)} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="font-cabinet-grotesk">
                          <SelectItem className="cursor-pointer" value={companyDetails.name || ""}>
                            {companyDetails.name}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={control}
                name="companyName"
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("Company", locale)} <span className="text-pink-primary">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={isPending}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-12 placeholder:text-sm focus-visible:ring-[2px]"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={control}
              name="vatNumber"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("VAT Number", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        readOnly={!!companyDetails?.vat}
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <FormField
          control={control}
          name="contactPerson"
          render={({ field }) => (
            <FormItem className="gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Contact person", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="postalAddress"
          render={({ field }) => (
            <FormItem className="gap-1 md:col-span-2">
              <div className="relative flex flex-col gap-2.5" ref={dropdownRef}>
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Postal Address", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    name={field.name}
                    onBlur={field.onBlur}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      handleAddressAutocomplete(e.target.value);
                    }}
                    onFocus={() => {
                      if (predictions.length > 0) {
                        setShowDropdown(true);
                      }
                    }}
                  />
                </FormControl>
                {showDropdown && predictions.length > 0 && (
                  <div className="border-border-light absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded border bg-white shadow-lg">
                    {predictions.map((prediction) => (
                      <div
                        key={prediction.place_id}
                        className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => handlePredictionSelect(prediction.place_id, prediction.description)}
                      >
                        {prediction.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="postalCode"
          render={({ field }) => (
            <FormItem className={cn("w-full gap-1", errors.city && !errors.postalCode && "mb-6")}>
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("ZIP Code", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="city"
          render={({ field }) => (
            <FormItem className={cn("w-full gap-1", errors.postalCode && !errors.city && "mb-6")}>
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("City", locale)} <span className="text-pink-primary -ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        {!addressId && showPrefillCheckbox && (
          <div className="md:col-span-2">
            <OptIn
              disabled={isPending}
              label={__("Same as selected Shipping address.", locale)}
              onCheckedChange={(checked) => {
                if (checked && deliveryAddress) {
                  setValue("postalAddress", deliveryAddress.address_1 || "");
                  setValue("city", deliveryAddress.city || "");
                  setValue("postalCode", deliveryAddress.postal_code || "");
                } else {
                  setValue("postalAddress", "");
                  setValue("city", "");
                  setValue("postalCode", "");
                }
              }}
              id="select-shipping"
            />
          </div>
        )}
        <FormField
          control={control}
          name="message"
          render={({ field }) => (
            <FormItem className="w-full gap-1 md:col-span-2">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">{__("Comments", locale)} </FormLabel>
                <FormControl>
                  <Textarea
                    disabled={isPending}
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light min-h-[157px] resize-none bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                    placeholder={__(
                      "Add any useful delivery details here (access code, floor number, landmarks...).",
                      locale
                    )}
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end md:col-span-2">
          <Button
            disabled={isPending}
            size="md"
            variant="rounded"
            className="w-full text-base font-semibold sm:w-fit"
            type="submit"
          >
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            {__("Save", locale)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BillingAddressForm;
